import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ContestResourceDocument } from '@isbd/shared';
import { CONTEST_RESOURCE_CATEGORIES } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { signContestResourceDocumentUrl } from '@/lib/bunnyStorage';
import { useToast } from '@/features/ui/toast/useToast';

const CATEGORY_LABELS = Object.fromEntries(CONTEST_RESOURCE_CATEGORIES.map((c) => [c.key, c.label]));

export function ContestDocumentationPage() {
  const toast = useToast();
  const [docs, setDocs] = useState<ContestResourceDocument[]>([]);
  const [category, setCategory] = useState<string>('all');
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const { data, error: err } = await supabase
        .from('contest_resource_documents')
        .select(
          'id, title, description, category, contest_id, file_name, storage_path, external_url, is_published, sort_order, created_at',
        )
        .eq('is_published', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (err) toast.error(err.message);
      else setDocs((data ?? []) as ContestResourceDocument[]);
    })();
  }, [toast]);

  const filtered = useMemo(() => {
    if (category === 'all') return docs;
    return docs.filter((d) => d.category === category);
  }, [docs, category]);

  async function openDoc(doc: ContestResourceDocument) {
    setOpening(doc.id);
    try {
      if (doc.external_url) {
        window.open(doc.external_url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (!doc.storage_path) {
        toast.error('Aucun fichier associé à ce document.');
        return;
      }
      const url = await signContestResourceDocumentUrl({ storagePath: doc.storage_path });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ouverture impossible');
    } finally {
      setOpening(null);
    }
  }

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">
            Base de documents de référence : règlements, guides et modèles pour préparer votre candidature.
          </p>
        </div>
        <div className="u-actions">
          <Link className="btn btn-ghost" to="/app/concours">
            Retour
          </Link>
          <Link className="btn btn-primary" to="/app/concours/inscription">
            S’inscrire aux concours
          </Link>
        </div>
      </div>

      <section className="u-card" style={{ marginBottom: '1rem' }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="doc-category">Filtrer par catégorie</label>
          <select id="doc-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">Toutes les catégories</option>
            {CONTEST_RESOURCE_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      {filtered.length === 0 ? (
        <p className="u-empty">
          {docs.length === 0
            ? 'Aucun document publié pour le moment. Revenez bientôt ou contactez l’équipe ISBD.'
            : 'Aucun document dans cette catégorie.'}
        </p>
      ) : (
        <ul className="u-list">
          {filtered.map((doc) => (
            <li key={doc.id} className="u-list-item" style={{ alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span className="chip" style={{ marginBottom: '0.35rem' }}>
                  {CATEGORY_LABELS[doc.category] ?? doc.category}
                </span>
                <strong style={{ display: 'block' }}>{doc.title}</strong>
                {doc.description ? (
                  <p className="muted" style={{ margin: '0.35rem 0 0' }}>
                    {doc.description}
                  </p>
                ) : null}
                {doc.file_name ? (
                  <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                    {doc.file_name}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="btn btn-primary"
                disabled={opening === doc.id}
                onClick={() => void openDoc(doc)}
              >
                {opening === doc.id ? 'Ouverture…' : 'Consulter'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
